import { ReactNode } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "app/components/ui/tabs/Tabs";
import Typography from "app/pages/.shared/Typography/Typography";
import { FormattedMessage } from "react-intl";
import classNames from "classnames";
import styles from "./AuthTabs.module.scss";
import loadable from "@loadable/component";
import { useLocation, useNavigate } from "react-router-dom";

// chargés à la demande : ces formulaires embarquent Formik + Yup et ne sont jamais visibles au premier rendu
const LoginContainer = loadable(
	() => import(/* webpackChunkName: "auth-login" */ "app/pages/Auth/Signin/LoginContainer")
);
const SignupContainer = loadable(
	() => import(/* webpackChunkName: "auth-signup" */ "app/pages/Auth/Signup/SignupContainer")
);

type AuthTabsProps = {
	onValueChange?: (value: string) => void;
	onAuthSuccess?: (...args: unknown[]) => void;
	className?: string;
	signupFooter?: ReactNode;
	signinFooter?: ReactNode;
	defaultValue?: "signup" | "login";
	value?: "signup" | "login";
	navigateOnChange?: boolean;
	homeOnResetPassword?: boolean;
};

type AuthContainerProps = {
	onAuthSuccess?: (...args: unknown[]) => void;
	footer?: ReactNode;
};

const SignupContainerTyped = SignupContainer as unknown as React.ComponentType<AuthContainerProps>;
const LoginContainerTyped = LoginContainer as unknown as React.ComponentType<
	AuthContainerProps & { homeRedirection?: boolean }
>;

export const AuthTabs = ({
	onValueChange,
	onAuthSuccess,
	className,
	signupFooter,
	signinFooter,
	defaultValue = "signup",
	value,
	navigateOnChange = false,
	homeOnResetPassword = true,
}: AuthTabsProps) => {
	const navigate = useNavigate();
	const location = useLocation();

	const handleValueChange = (value: string) => {
		if (navigateOnChange) {
			navigate(`${location.pathname.replace(/\/(login|signup)$/, "")}/${value}`);
		}

		if (typeof onValueChange === "function") {
			onValueChange(value);
		}
	};

	const tabsValueProps = typeof value === "string" ? { value } : { defaultValue };

	return (
		<Tabs
			{...tabsValueProps}
			className={classNames(styles["auth-tabs"], className)}
			onValueChange={handleValueChange}
		>
			<TabsList fullWidth className={styles["auth-tabs__list"]}>
				<TabsTrigger
					className={styles["auth-tabs__trigger"]}
					value="signup"
					variant="large"
					isBold={false}
					data-testid="switch-to-signUp"
				>
					<FormattedMessage id="home.signup.button.label" />
				</TabsTrigger>

				<TabsTrigger
					className={styles["auth-tabs__trigger"]}
					value="login"
					variant="large"
					isBold={false}
					data-testid="switch-to-login"
				>
					<FormattedMessage id="home.signin.button.label" />
				</TabsTrigger>
			</TabsList>

			<TabsContent value="signup" className={styles["auth-tabs__content"]} tabIndex={-1}>
				<Typography variant="regular" component="span">
					<SignupContainerTyped onAuthSuccess={onAuthSuccess} footer={signupFooter} />
				</Typography>
			</TabsContent>

			<TabsContent value="login" className={styles["auth-tabs__content"]} tabIndex={-1}>
				<Typography variant="regular" component="span">
					<LoginContainerTyped
						onAuthSuccess={onAuthSuccess}
						footer={signinFooter}
						homeRedirection={homeOnResetPassword}
					/>
				</Typography>
			</TabsContent>
		</Tabs>
	);
};
